import { flatten } from 'lodash';
import { useMemo } from 'react';
import { FieldName, FieldNames, FilterKeyNames } from 'v2/core/entities/fields';
import {
  FilterUnion,
  WhereClause,
} from 'v2/core/services/queryBuilder/whereClause';

const useQueryInspector = (query: WhereClause, fieldName: FieldName) => {
  const memoizedFilters = useMemo(
    () => getFiltersFromQuery(query).flat(3),
    [query],
  );

  const fieldFilters = getFieldSpecificFilters(fieldName, memoizedFilters);
  const mappedFieldFilters = getMappedFieldSpecificFilters(fieldFilters);

  const fieldSpecificFilters = useMemo((): FilterUnion[] => {
    if (fieldName === FieldNames.Metadata) {
      return mappedFieldFilters;
    }
    return getFlattenedFilters(mappedFieldFilters);
  }, [fieldName, mappedFieldFilters]);

  return fieldSpecificFilters;
};

const getFieldSpecificFilters = (
  fieldName: FieldName,
  queryFilters: FilterUnion[],
) => {
  switch (fieldName) {
    case FieldNames.Theme:
      return queryFilters.filter((queryFilter) =>
        checkKey(queryFilter, FilterKeyNames.ThemeFilter),
      );

    case FieldNames.Entity:
    case FieldNames.EntitySentiment:
      return queryFilters.filter((queryFilter) =>
        checkKey(queryFilter, FilterKeyNames.EntityFilter),
      );

    case FieldNames.Reason:
      return queryFilters.filter((queryFilter) =>
        checkKey(queryFilter, FilterKeyNames.ReasonFilter),
      );

    case FieldNames.ReviewSentiment:
      return queryFilters.filter((queryFilter) =>
        checkKey(queryFilter, FilterKeyNames.ReviewSentimentFilter),
      );

    case FieldNames.Language:
      return queryFilters.filter((queryFilter) =>
        checkKey(queryFilter, FilterKeyNames.LanguageFilter),
      );

    case FieldNames.Source:
      return queryFilters.filter((queryFilter) =>
        checkKey(queryFilter, FilterKeyNames.SourceFilter),
      );

    case FieldNames.Metadata:
      return queryFilters.filter((queryFilter) =>
        checkKey(queryFilter, FilterKeyNames.MetadataFilter),
      );
    case FieldNames.Category:
      return queryFilters.filter((queryFilter) =>
        checkKey(queryFilter, FilterKeyNames.CategoryFilter),
      );
    default:
      throw Error(`Invalid FieldName type: ${fieldName}`);
  }
};

const getMappedFieldSpecificFilters = (
  filters: FilterUnion[],
): FilterUnion[] => {
  return filters.map((filter) => {
    return {
      entityFilter: filter.entityFilter,
      reasonFilter: filter.reasonFilter,
      reviewSentimentFilter: filter.reviewSentimentFilter,
      metadataFilter: filter.metadataFilter,
      languageFilter: filter.languageFilter,
      labelHierarchyFilter: filter.labelHierarchyFilter,
      sourceFilter: filter.sourceFilter,
      hasReasonFilter: filter.hasReasonFilter,
      hasAspectFilter: filter.hasAspectFilter,
      hasClusterFilter: filter.hasClusterFilter,
      hybridSearchFilter: filter.hybridSearchFilter,
      hasUserContentFilter: filter.hasUserContentFilter,
      categoryFilter: filter.categoryFilter,
      hasMetadataFilter: filter.hasMetadataFilter,
    };
  });
};

const checkKey = (obj: FilterUnion, key: string) =>
  Object.keys(obj ?? {}).includes(key);

export const getFiltersFromQuery = (query: WhereClause): FilterUnion[] => {
  const hasFilterOrParams = query.op?.params || query.filter;

  if (query.exclude || !hasFilterOrParams) {
    return [];
  }

  // Call a recursive function to get all filters from the query
  if (query.op?.params) {
    return query.op.params.map((q) => getFiltersFromQuery(q) as FilterUnion);
  }

  return query.filter ? [query.filter] : [];
};

const getFlattenedFilters = (filters: FilterUnion[] | undefined) => {
  return filters
    ? (flatten(
        filters
          .flatMap((p) => Object.values(p ?? {}))
          .flatMap((p) => Object.values(p ?? {}))
          .filter(Boolean),
      ) as FilterUnion[])
    : [];
};

export default useQueryInspector;
